import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["controlId", "error", "readOnly", "required"];
import PropTypes from 'prop-types';
import React, { forwardRef, useContext } from 'react';
import { logDeprecation } from '../../util/logging';
import { FormFieldContext } from '../FormField/FormField.context';
import { StyledFormControl } from './FormControl.styles';

/**
 * @related ['Checkbox', 'Input', 'Radio', 'Select', 'Textarea']
 * @since 8.0.0
 */
export var FormControl = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var controlId = _ref.controlId,
    error = _ref.error,
    readOnly = _ref.readOnly,
    required = _ref.required,
    rest = _objectWithoutProperties(_ref, _excluded);
  var context = useContext(FormFieldContext);
  if (typeof controlId !== 'undefined') {
    logDeprecation("`controlId` should not be used on form controls, instead use the native `id` attribute");
  }
  return /*#__PURE__*/React.createElement(StyledFormControl, _extends({
    ref: forwardedRef,
    id: typeof controlId !== 'undefined' ? controlId : context.controlId,
    "aria-invalid": typeof error !== 'undefined' ? error : context.error,
    "aria-describedby": context.descriptionId,
    disabled: context.disabled,
    required: typeof required !== 'undefined' ? required : context.required,
    "aria-required": typeof required !== 'undefined' ? required : context.required,
    readOnly: typeof readOnly !== 'undefined' ? readOnly : context.readOnly
  }, rest));
});
FormControl.displayName = 'FormControl';
FormControl.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Indicate that the value entered is invalid using the native
   * [`aria-invalid`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-invalid_attribute)
   * attribute.
   * This will be set automatically by the `error` prop.
   */
  'aria-invalid': PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * If there are multiple labels for a control, it is best to set the native
   * [`aria-describedby`](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-describedby_attribute)
   * with each of the label ids. You can read more [here](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-describedby_attribute).
   * Can be controlled by `FormField` context.
   */
  'aria-describedby': PropTypes.string,
  /**
   * @deprecated Use the native `id` attributed instead.
   */
  'controlId': PropTypes.string,
  /**
   * Signals that there is an error with the value and sets the
   * [aria-invalid](https://www.w3.org/TR/wai-aria/#aria-invalid) property.
   */
  'error': PropTypes.bool,
  /**
   * The native [`id`](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id)
   * attribute.
   * Can be controlled by `FormField` context.
   */
  'id': PropTypes.string,
  /**
   * Mark the control as required with the native [`required`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/required)
   * attribute.
   * Can be controlled by `FormField` context.
   */
  'required': PropTypes.bool,
  /**
   * Mark the control as disabled with the native [`disabled`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled)
   * attribute.
   * Can be controlled by `FormField` context.
   */
  'disabled': PropTypes.bool,
  /**
   * Mark the input as read-only with the native [`readonly`](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly)
   * attribute.
   * Can be controlled by `FormField` context.
   *
   * @since 8.27.0
   */
  'readOnly': PropTypes.bool
} : {};